/**
 * #1124 part B1 — main-thread long tasks for the Data pressure card.
 *
 * One `longtask` observer (tasks over 50 ms; Chromium only, feature-detected)
 * runs while a card is mounted. Each task's duration goes into the one-second
 * slot of the second it STARTED in, so "the last 10 s" is the same whatever
 * the sampler's tick spacing; the visibility-aware interval's return tick and
 * the tick after it are not a second apart.
 *
 * Fixed size: three 10-slot typed arrays. The browser allocates the entries;
 * none is kept.
 */

const WINDOW_S = 10;
const blockedMs = new Float64Array(WINDOW_S);
const counts = new Uint16Array(WINDOW_S);
const slotSecond = new Float64Array(WINDOW_S).fill(-1);
let observer: PerformanceObserver | null = null;

function epochSecond(perfMs: number): number {
  return Math.floor((performance.timeOrigin + perfMs) / 1000);
}

function supported(): boolean {
  return typeof PerformanceObserver !== 'undefined'
    && (PerformanceObserver.supportedEntryTypes?.includes('longtask') ?? false);
}

function clear(): void {
  blockedMs.fill(0);
  counts.fill(0);
  slotSecond.fill(-1);
}

function record(startTime: number, duration: number): void {
  const sec = epochSecond(startTime);
  const slot = sec % WINDOW_S;
  if (slotSecond[slot] !== sec) {
    slotSecond[slot] = sec;
    blockedMs[slot] = 0;
    counts[slot] = 0;
  }
  blockedMs[slot] += duration;
  if (counts[slot] < 0xffff) counts[slot] += 1;
}

export function startLongTasks(): void {
  if (observer || !supported()) return;
  clear();
  observer = new PerformanceObserver((list) => {
    for (const e of list.getEntries()) record(e.startTime, e.duration);
  });
  observer.observe({ type: 'longtask', buffered: false });
}

export function stopLongTasks(): void {
  observer?.disconnect();
  observer = null;
  clear();
}

export function longTaskObserverActive(): boolean {
  return observer != null;
}

/** Long tasks that started in the last 10 whole seconds; null when the browser
 *  doesn't report them or no card is observing. */
export function readLongTasks(): { blockedMs: number; count: number } | null {
  if (!observer) return null;
  const now = epochSecond(performance.now());
  let ms = 0;
  let n = 0;
  for (let i = 0; i < WINDOW_S; i += 1) {
    if (slotSecond[i] > now - WINDOW_S && slotSecond[i] <= now) {
      ms += blockedMs[i];
      n += counts[i];
    }
  }
  return { blockedMs: ms, count: n };
}
